import { Controller } from "@hotwired/stimulus"

const RECOMPUTE_INTERVAL_MS = 60_000

export default class extends Controller {
  static targets = [
    "dateInput",
    "fromTime",
    "toTime",
    "timezone",
    "fromTimeError",
    "toTimeError"
  ]
  static values = { timezoneNames: Object }

  connect() {
    this.updateTimeOptions = this.updateTimeOptions.bind(this)
    this.hideFromTimeError = this.hideFromTimeError.bind(this)
    this.hideToTimeError = this.hideToTimeError.bind(this)

    this.dateInputTarget.addEventListener("change", this.updateTimeOptions)
    this.timezoneTarget.addEventListener("change", this.updateTimeOptions)
    this.fromTimeTarget.addEventListener("change", this.hideFromTimeError)
    this.toTimeTarget.addEventListener("change", this.hideToTimeError)
    document.addEventListener("visibilitychange", this.updateTimeOptions)

    this.recomputeTimer = setInterval(this.updateTimeOptions, RECOMPUTE_INTERVAL_MS)
    requestAnimationFrame(this.updateTimeOptions)
  }

  disconnect() {
    this.dateInputTarget.removeEventListener("change", this.updateTimeOptions)
    this.timezoneTarget.removeEventListener("change", this.updateTimeOptions)
    this.fromTimeTarget.removeEventListener("change", this.hideFromTimeError)
    this.toTimeTarget.removeEventListener("change", this.hideToTimeError)
    document.removeEventListener("visibilitychange", this.updateTimeOptions)

    if (this.recomputeTimer) {
      clearInterval(this.recomputeTimer)
      this.recomputeTimer = null
    }
  }

  updateTimeOptions() {
    const ianaName = this.selectedTimezoneIanaName()
    if (!ianaName) return

    const isToday = this.dateInputTarget.value === this.todayInZone(ianaName)
    const minTime = isToday ? this.currentTimeInZone(ianaName) : null

    this.applyMinTime(this.fromTimeTarget, minTime, this.fromTimeErrorTarget)
    this.applyMinTime(this.toTimeTarget, minTime, this.toTimeErrorTarget)
  }

  hideFromTimeError() {
    this.hideError(this.fromTimeErrorTarget)
  }

  hideToTimeError() {
    this.hideError(this.toTimeErrorTarget)
  }

  selectedTimezoneIanaName() {
    const timezoneId = String(this.timezoneTarget.value)
    return this.timezoneNamesValue[timezoneId]
  }

  todayInZone(ianaName) {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: ianaName,
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    }).format(new Date())
  }

  currentTimeInZone(ianaName) {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: ianaName,
      hour12: false,
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date())
  }

  applyMinTime(selectElement, minTime, errorElement) {
    const tomSelect = selectElement.tomselect
    if (!tomSelect) return

    Object.entries(tomSelect.options).forEach(([value, option]) => {
      if (!value) return
      const shouldDisable = minTime !== null && value < minTime
      if (Boolean(option.disabled) === shouldDisable) return
      tomSelect.updateOption(value, { ...option, disabled: shouldDisable })
    })

    const currentValue = tomSelect.getValue()
    if (currentValue && tomSelect.options[currentValue]?.disabled) {
      tomSelect.clear(true)
      this.showError(errorElement)
    } else if (minTime === null) {
      this.hideError(errorElement)
    }

    tomSelect.refreshOptions(false)
  }

  showError(errorElement) {
    if (!errorElement) return
    errorElement.classList.remove("hidden")
  }

  hideError(errorElement) {
    if (!errorElement) return
    errorElement.classList.add("hidden")
  }
}
